
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function SolarSystemMonitoringSetupPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Solar System Monitoring Setup',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional solar monitoring system installation in Fresno, CA. Real-time production tracking, energy usage monitoring, and system performance alerts.',
    url: `${siteUrl}/services/renewables-ev/solar-system-monitoring-setup`,
  };

  return (
    <>
      <SEOHead
        title="Solar System Monitoring Setup Fresno CA | Production Tracking"
        description="Professional solar monitoring system installation in Fresno, CA. Real-time production tracking, energy usage monitoring, and performance alerts for solar systems."
        canonical="/services/renewables-ev/solar-system-monitoring-setup"
        keywords="solar monitoring Fresno, solar production tracking, Enphase monitoring, SolarEdge monitoring"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Solar System Monitoring Setup — Fresno, CA"
        heroSubtitle="Track your solar production and energy savings in real-time"
        heroImage="https://public.readdy.ai/ai/img_res/9c5afe3cfab8f2cdea4431f6478cc160.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20solar%20monitoring%20gateway%20device%20mounted%20on%20wall%20with%20LED%20status%20indicators%20and%20WiFi%20antenna%20showing%20electrical%20connections%20to%20inverter%20and%20production%20meter%20professional%20California%20residential%20installation%20no%20people&width=700&height=600&seq=solar-monitoring-fresno-001&orientation=portrait"
        fresnoContent="Solar monitoring systems transform your Fresno solar installation from a black box into a transparent, data-rich energy management tool. Track real-time production, identify underperforming panels, monitor energy consumption, and verify your PG&E bill savings. Our certified electricians install and configure monitoring systems for all major solar brands including Enphase, SolarEdge, SMA, and Fronius throughout Fresno County including Clovis, Sanger, and surrounding areas. With Fresno's abundant sunshine, monitoring ensures your system delivers expected performance and quickly identifies any issues affecting your solar investment and energy savings."
        features={[
          {
            icon: 'ri-line-chart-line',
            title: 'Real-Time Production',
            description: 'Live tracking of solar energy generation and consumption',
          },
          {
            icon: 'ri-smartphone-line',
            title: 'Mobile App Access',
            description: 'Monitor your system from anywhere via smartphone',
          },
          {
            icon: 'ri-grid-line',
            title: 'Panel-Level Monitoring',
            description: 'Individual panel performance with microinverter systems',
          },
          {
            icon: 'ri-alarm-warning-line',
            title: 'Performance Alerts',
            description: 'Automatic notifications for system issues or outages',
          },
          {
            icon: 'ri-bar-chart-line',
            title: 'Historical Data',
            description: 'Track daily, monthly, and annual production trends',
          },
          {
            icon: 'ri-money-dollar-circle-line',
            title: 'Savings Calculator',
            description: 'See your PG&E bill savings and environmental impact',
          },
        ]}
        whatsIncluded={[
          'Free consultation on monitoring system options',
          'Professional monitoring gateway installation',
          'WiFi connectivity setup and network configuration',
          'Current transformer (CT) installation for consumption monitoring',
          'Production meter wiring and integration',
          'Mobile app installation and account setup',
          'Dashboard customization and alert configuration',
          'Panel-level monitoring setup (for microinverter systems)',
          'Historical data import and baseline establishment',
          'Owner training on app features and data interpretation',
          'Integration with battery storage monitoring (if applicable)',
          'Troubleshooting and connectivity optimization',
          'Complete system testing and verification',
          '2-year monitoring system warranty',
        ]}
        faqs={[
          {
            question: 'What solar monitoring systems are available for Fresno homes?',
            answer:
              'Major solar monitoring systems include Enphase Enlighten (for Enphase microinverters), SolarEdge monitoring (for SolarEdge inverters), SMA Sunny Portal (for SMA inverters), and Fronius Solar.web (for Fronius inverters). Third-party systems like Sense and Emporia provide consumption monitoring for any solar system. Enphase and SolarEdge offer the most comprehensive monitoring with panel-level data, while string inverter systems provide whole-system production data. We help you choose the best monitoring solution based on your inverter type, desired features, and budget during our free consultation.',
          },
          {
            question: 'How much does solar monitoring system installation cost in Fresno?',
            answer:
              'Solar monitoring costs vary by system type. Basic inverter-integrated monitoring (SolarEdge, Enphase) is typically included with your solar installation at no additional cost. Advanced consumption monitoring with current transformers adds $300-$600. Third-party systems like Sense ($300-$400) or Emporia ($150-$250) provide detailed consumption tracking. Professional installation and WiFi setup adds $200-$400. Most Fresno solar installations include basic production monitoring, with consumption monitoring as an optional upgrade. All monitoring equipment and installation costs are included in the 30% federal solar tax credit when installed with your solar system.',
          },
          {
            question: 'Can I monitor individual solar panel performance in Fresno?',
            answer:
              'Yes, but only with microinverter systems (Enphase) or power optimizer systems (SolarEdge). These systems provide panel-level monitoring showing production from each individual panel, helping identify shading issues, soiling, or panel failures. String inverter systems (SMA, Fronius) only show total system production. For Fresno homes with complex roofs, chimneys, or nearby trees, panel-level monitoring is valuable for optimizing performance. We can retrofit existing string inverter systems with power optimizers to add panel-level monitoring, though this adds $150-$250 per panel to system costs.',
          },
          {
            question: 'What data does solar monitoring show for Fresno systems?',
            answer:
              "Comprehensive solar monitoring displays: real-time power production (kW), daily/monthly/annual energy generation (kWh), individual panel performance (with microinverters), home energy consumption, grid import/export, battery charge status (if applicable), system efficiency, environmental impact (CO2 offset), estimated PG&E bill savings, weather data, and historical performance trends. Advanced systems provide alerts for underperforming panels, inverter issues, or grid outages. For Fresno's variable weather, monitoring helps you understand seasonal production patterns and verify your system meets expected performance based on local solar irradiance.",
          },
          {
            question: 'How does solar monitoring help identify problems in Fresno?',
            answer:
              "Solar monitoring quickly identifies performance issues before they significantly impact your savings. Common problems detected include: individual panel failures or shading (panel-level monitoring), inverter malfunctions, grid connection issues, soiling from Fresno's dust and agricultural activity, and unexpected system shutdowns. Most monitoring systems send automatic alerts when production drops below expected levels. For example, if your system typically produces 40kWh daily in summer but suddenly drops to 30kWh, monitoring alerts you to investigate. Early problem detection prevents prolonged production losses and ensures your solar investment delivers expected returns.",
          },
          {
            question: 'Can I add monitoring to my existing Fresno solar system?',
            answer:
              "Yes! Most existing solar systems can be retrofitted with monitoring. If you have Enphase microinverters or SolarEdge inverters, monitoring is built-in and just needs WiFi gateway installation ($200-$400). String inverter systems can add third-party monitoring like Sense or Emporia for consumption tracking ($300-$600 installed). For panel-level monitoring on existing string inverter systems, you'd need to add power optimizers ($150-$250 per panel), which is usually cost-prohibitive. We assess your current system during our free consultation and recommend the most cost-effective monitoring solution for your specific setup.",
          },
        ]}
        ctaText="Get Your Free Solar Monitoring Quote"
      />
    </>
  );
}
